<template>
  <div class="lawyer-detail-page">
    <div class="main-content">
      <!-- 第一部分：英雄区域 -->
      <section class="hero-section">
        <div class="container">
          <AppHeader />
          
          <div class="hero-content">
            <div class="lawyer-hero-photo animate">
              <div v-if="imageLoading" class="image-placeholder">
                <div class="loading-spinner"></div>
                <span>加载中...</span>
              </div>
              <img 
                :src="currentLawyer?.image" 
                :alt="currentLawyer?.name"
                @load="onImageLoad"
                @error="onImageError"
                v-show="!imageLoading && !imageError"
                class="lawyer-image"
              >
              <div v-if="imageError" class="image-placeholder error">
                <span>图片加载失败</span>
              </div>
            </div>
            <div class="lawyer-hero-info animate">
              <div class="hero-name-wrapper">
                <h1 class="hero-name-cn">{{ currentLawyer?.name }}</h1>
                <h2 class="hero-position">{{ currentLawyer?.position }}</h2>
              </div>
              <h3 class="hero-name-en">{{ currentLawyer?.nameEn }}</h3>
              <h3 class="hero-law-firm">{{ currentLawyer?.law_firm }}</h3>
              <div class="hero-contact">
                <div class="contact-item" v-if="currentLawyer?.email">
                  <span class="contact-label">邮箱：</span>
                  <span class="contact-value">{{ currentLawyer?.email }}</span>
                </div>
                <div class="contact-item" v-if="currentLawyer?.area.length > 0">
                  <span class="contact-label">工作地点：</span>
                  <span class="contact-value" v-for="(item,index) in currentLawyer?.area" :key="index">{{ item }}{{ index < currentLawyer?.area.length - 1 ? '、' : '' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <!-- 第二部分：律师详情内容 -->
      <section class="lawyer-content-section" ref="lawyerContentSection">
        <div class="container">
          <div class="lawyer-profile">
            <div class="side-menu animate-scroll">
              <div class="side-menu-items">
                <div class="side-menu-item" :class="{ active: activeSection === 0 }" @click="handleClick('intro', 0)">
                  <div class="indicator" v-show="activeSection === 0"></div>
                  <span>个人简介</span>
                </div>
                <!-- <div class="side-menu-item" :class="{ active: activeSection === 1 }" @click="handleClick('positions', 1)">
                  <div class="indicator" v-show="activeSection === 1"></div>
                  <span>社会职务</span>
                </div>
                <div class="side-menu-item" :class="{ active: activeSection === 2 }" @click="handleClick('awards', 2)">
                  <div class="indicator" v-show="activeSection === 2"></div>
                  <span>所获奖项</span>
                </div> -->
                <div class="side-menu-item" :class="{ active: activeSection === 3 }" @click="handleClick('articles', 3)">
                  <div class="indicator" v-show="activeSection === 3"></div>
                  <span>相关文章</span>
                </div>
              </div>
            </div>
            
            <div class="lawyer-content animate-scroll">
              <div id="intro" class="section-content">
                <!-- <h3 class="section-title">个人简介</h3> -->
                <div class="section-text" v-html="formatText(currentLawyer?.intro)"></div>
              </div>
              
              <div class="section-divider"></div>
              
              <!-- <div id="positions" class="section-content">
                <h3 class="section-title">社会职务</h3>
                <div class="section-text">
                  <p v-for="(position, index) in currentLawyer?.positions" :key="index">{{ position }}</p>
                </div>
              </div>
              
              <div class="section-divider"></div>
              
              <div id="awards" class="section-content">
                <h3 class="section-title">所获奖项</h3>
                <div class="section-text">
                  <p v-for="(award, index) in currentLawyer?.awards" :key="index">{{ award }}</p>
                </div>
              </div> -->
              
              <!-- <div class="section-divider"></div> -->
              
              <div id="articles" class="section-content">
                <h3 class="section-title">相关文章</h3>
                <div class="section-text article-list">
                  <div v-if="articlesLoading" class="loading-articles">
                    加载中...
                  </div>
                  <div v-else-if="lawyerArticles.length === 0" class="no-articles">
                    暂无相关文章
                  </div>
                  <div v-else class="article-items">
                    <div class="article-item" v-for="article in lawyerArticles" :key="article.id" @click="goToArticleDetail(article.id)">
                      <div class="article-info">
                        <a href="javascript:void(0)" class="article-title">{{ article.title }}</a>
                        <div class="article-meta">
                          <span class="article-date">{{ formatDate(article.publish_time) }}</span>
                          <span v-if="article.author" class="article-author">{{ article.author }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="section-divider"></div>
            </div>
          </div>
        </div>
      </section>
    </div>
    <div class="footer-wrapper">
      <AppFooter />
    </div>
  </div>
</template>

<script>
import AppFooter from '@/components/AppFooter.vue';
import AppHeader from '@/components/AppHeader.vue';
import { getLawyerInfo, getArticleList } from '@/api/index';
import { RESPONSE_CODE } from '@/api/constants';
import pinyin from 'pinyin';

export default {
  name: 'LawyerDetail',
  components: {
    AppFooter,
    AppHeader
  },
  data() {
    return {
      observers: [],
      activeSection: 0,
      sectionObservers: [],
      currentLawyer: null,
      lawyerArticles: [],
      articlesLoading: false,
      imageLoading: true,
      imageError: false
    }
  },
  created() {
    this.loadLawyerData();
  },
  mounted() {
    this.initScrollAnimations();
    this.initSectionObservers();
    
    // 首屏动画自动播放
    const animateElements = document.querySelectorAll('.animate');
    animateElements.forEach((el, index) => {
      setTimeout(() => {
        el.classList.add('visible');
      }, 300 * index);
    });
    // 头部动画
    this.$nextTick(() => {
      const header = this.$el.querySelector('.logo-area.animate-scroll');
      if (header) header.classList.add('visible');
    });
  },
  beforeUnmount() {
    // 清除所有观察者
    this.observers.forEach(observer => observer.disconnect());
    this.sectionObservers.forEach(observer => observer.disconnect());
  },
  watch: {
    '$route.params.id': function() {
      this.loadLawyerData();
    }
  },
  methods: {
    // 将中文姓名转换为拼音
    convertToPinyin(chineseName) {
      if (!chineseName) return '';
      
      // 使用pinyin库转换，不带声调
      const pinyinArray = pinyin(chineseName, {
        style: pinyin.STYLE_NORMAL, // 不带声调
        heteronym: false, // 不显示多音字的多个读音
        segment: true // 启用分词
      });
      
      // 将二维数组扁平化
      const pinyinList = pinyinArray.map(item => item[0]);
      
      if (pinyinList.length === 0) return '';
      
      // 第一个字（姓氏）全大写
      const surname = pinyinList[0].toUpperCase();
      
      // 名字部分：第一个字首字母大写，其余字全部小写
      const givenNameParts = pinyinList.slice(1);
      let givenName = '';
      if (givenNameParts.length > 0) {
        // 第一个字首字母大写，其余小写
        const firstGivenName = givenNameParts[0].charAt(0).toUpperCase() + givenNameParts[0].slice(1).toLowerCase();
        // 其余字全部小写
        const restGivenNames = givenNameParts.slice(1).map(name => name.toLowerCase());
        givenName = firstGivenName + restGivenNames.join('');
      }
      
      // 姓氏和名字之间用空格连接
      return givenName ? `${surname} ${givenName}` : surname;
    },
    async loadLawyerData() {
      try {
        const lawyerId = this.$route.params.id;
        const res = await getLawyerInfo(lawyerId);
        if (res && res.data) {
          const apiData = res.data;
          this.currentLawyer = {
            id: apiData.id,
            name: apiData.u_name,
            nameEn: apiData.u_name_en || this.convertToPinyin(apiData.u_name),
            title: apiData.u_introduction,
            intro: apiData.u_content,
            image: apiData.u_image,
            awards: apiData.awards || [],
            articles: apiData.articles || [],
            law_firm: apiData.law_firm || '',
            position: apiData.position || '',
            email: apiData.email || '',
            area: apiData.area || ''
          };
          
          // 重置图片加载状态
          this.imageLoading = true;
          this.imageError = false;
          
          this.loadLawyerArticles(lawyerId);
        }
      } catch (e) {
        // 可加错误提示
        this.currentLawyer = { name: '数据加载失败', title: '', intro: '' };
        this.imageLoading = false;
        this.imageError = true;
      }
    },
    async loadLawyerArticles(lawyerId) {
      this.articlesLoading = true;
      try {
        const res = await getArticleList({ lawyer_id: lawyerId });
        if (res.code === RESPONSE_CODE.SUCCESS_ALT || res.code === RESPONSE_CODE.SUCCESS) {
          let articlesData = [];
          
          if (res.data && res.data.data && Array.isArray(res.data.data)) {
            articlesData = res.data.data;
          } else if (res.data && Array.isArray(res.data)) {
            articlesData = res.data;
          }
          
          this.lawyerArticles = articlesData.map(item => ({
            id: item.id,
            title: item.title || '无标题',
            cover: item.cover_image || item.cover || item.image || '',
            publish_time: item.createtime || item.create_time || item.publish_time || '',
            author: item.author || ''
          }));
        } else {
          console.error('获取律师相关文章失败:', res.msg);
        }
      } catch (error) {
        console.error('获取律师相关文章失败:', error);
      } finally {
        this.articlesLoading = false;
      }
    },
    initScrollAnimations() {
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.1
      };
      
      const sections = [
        this.$refs.lawyerContentSection
      ];
      
      sections.forEach(section => {
        if (section) {
          const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                const animatedElements = entry.target.querySelectorAll('.animate-scroll');
                animatedElements.forEach(el => {
                  el.classList.add('visible');
                });
              }
            });
          }, options);
          
          observer.observe(section);
          this.observers.push(observer);
        }
      });
    },
    initSectionObservers() {
      const options = {
        root: null,
        rootMargin: '-100px 0px -70% 0px',
        threshold: 0
      };
      
      const sectionIds = ['intro', 'positions', 'awards', 'articles'];
      
      sectionIds.forEach((id, index) => {
        const element = document.getElementById(id);
        if (element) {
          const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                this.activeSection = index;
              }
            });
          }, options);
          
          observer.observe(element);
          this.sectionObservers.push(observer);
        }
      });
    },
    scrollToSection(id, sectionIndex) {
      // 直接设置当前激活的部分
      this.activeSection = sectionIndex;
      
      const element = document.getElementById(id);
      if (element) {
        window.scrollTo({
          top: element.offsetTop - 100,
          behavior: 'smooth'
        });
      }
    },
    formatText(text) {
      return text || '';
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      
      try {
        if (!isNaN(dateStr)) {
          if (dateStr.toString().length === 10) {
            dateStr = parseInt(dateStr) * 1000;
          }
          const date = new Date(parseInt(dateStr));
          return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
        }
        
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) {
          const parts = dateStr.split(/[- :]/);
          if (parts.length >= 3) {
            const date = new Date(parts[0], parts[1] - 1, parts[2]);
            return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
          }
          return dateStr;
        }
        
        return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
      } catch (error) {
        console.error('日期格式化错误:', error, dateStr);
        return dateStr;
      }
    },
    goToArticleDetail(id) {
      this.$router.push(`/article/${id}`);
    },
    handleClick(id, sectionIndex) {
      // 先设置活动部分
      this.activeSection = sectionIndex;
      
      // 然后滚动到对应区域
      const element = document.getElementById(id);
      if (element) {
        window.scrollTo({
          top: element.offsetTop - 100,
          behavior: 'smooth'
        });
      }
    },
    onImageLoad() {
      this.imageLoading = false;
      this.imageError = false;
    },
    onImageError() {
      this.imageLoading = false;
      this.imageError = true;
    }
  }
}
</script>

<style scoped>
.lawyer-detail-page {
  margin: 0;
  padding: 0;
  font-family: Arial, sans-serif;
  background-color: rgba(247, 247, 247, 1);
  width: 1920px;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #FFFFFF;
}
.footer-wrapper {
  flex-shrink: 0;
}

/* 入场动画基础样式 */
.animate, .animate-scroll {
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 1s ease, transform 1s ease;
}

.animate.visible, .animate-scroll.visible {
  opacity: 1;
  transform: translateY(0);
}

/* 英雄区域样式 */
.hero-section {
  width: 1920px;
  min-height: 700px;
  background-color: #8E9296;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
  overflow: hidden;
}

.container {
  width: 1200px;
  margin: 0 auto;
  position: relative;
  min-height: 700px;
}

.hero-content {
  width: 1300px;
  padding: 105px 0 40px 0;
  position: relative;
  z-index: 3;
  min-height: 700px;
}

.hero-content::after {
  content: "";
  display: table;
  clear: both;
}

.lawyer-hero-photo {
  position: absolute;
  bottom: -10rpx;
  left: 180px;
  width: 500px;
  /* height: 450px; */
  z-index: 1;
  /* background-color: #8E9296; */
  overflow: visible;
}

.lawyer-hero-photo::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 500px;
  height: 1050px;
  background: url('~@/assets/image/light.png') center no-repeat;
  background-size: 100% 100%;
  z-index: -1;
}

.lawyer-hero-photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.lawyer-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #666;
  font-size: 14px;
  border-radius: 8px;
}

.image-placeholder.error {
  background-color: #fff2f0;
  color: #ff4d4f;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid #e6e6e6;
  border-top: 3px solid #3159B3;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 8px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.lawyer-hero-info {
  margin-left: 680px;
  color: #FFFFFF;
  padding-top: 100px;
  position: relative;
  z-index: 3;
}

.hero-name-wrapper {
  padding-bottom: 10px;
}

.hero-name-wrapper::after {
  content: "";
  display: table;
  clear: both;
}

.hero-name-cn {
  float: left;
  font-weight: 600;
  font-size: 40px;
  color: #FFFFFF;
  margin: 0;
  line-height: 1.2;
  letter-spacing: 15px;
}

.hero-position {
  float: left;
  font-weight: 400;
  font-size: 16px;
  color: #FFFFFF;
  margin: 0;
  padding: 25px 0 0 5px;
  display: block;
}

.hero-name-en {
  clear: both;
  font-weight: bold;
  font-size: 40px;
  color: #FFFFFF;
  margin: 0;
  line-height: 1.2;
  padding-bottom: 10px;
}
.hero-law-firm {
  padding-bottom: 130px;
  clear: both;
  font-weight: bold;
  font-size: 40px;
  color: #FFFFFF;
  margin: 0;
  line-height: 1.2;
}

.hero-contact {
  clear: both;
}

.contact-item {
  display: block;
  font-size: 16px;
  color: #FFFFFF;
  padding-bottom: 10px;
}

.contact-label {
  color: #FFFFFF;
  display: inline-block;
}

.contact-value {
  color: #FFFFFF;
  display: inline-block;
}

/* 律师详情内容部分样式 */
.lawyer-content-section {
  width: 1920px;
  background: #FFFFFF;
  padding: 40px 0;
  position: relative;
  overflow: hidden;
}

.lawyer-profile {
  display: flex;
  gap: 10px;
}

.side-menu {
  display: flex;
  position: relative;
  width: 120px;
  min-width: 120px;
}

.side-menu-items {
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
}

.side-menu-items::before {
  content: none;
}

.side-menu-item {
  font-size: 18px;
  line-height: 28px;
  color: #666666;
  cursor: pointer;
  transition: color 0.3s ease;
  padding: 16px 0;
  position: relative;
  padding-left: 20px;
  width: 100%;
  white-space: nowrap;
}

.side-menu-item::before {
  content: "";
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 1px;
  height: 28px;
  background-color: #CCCCCC;
}

.side-menu-item span {
  display: inline-block;
  width: 100%;
  white-space: nowrap;
}

.side-menu-item .indicator {
  position: absolute;
  left: -1px;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 28px;
  background-color: #3159B3;
}

.side-menu-item.active {
  color: #333333;
  font-weight: 600;
}

.lawyer-content {
  margin-left: 180px;
  flex: 1;
}

.section-content {
  margin-bottom: 20px;
}

.section-title {
  font-size: 26px;
  font-weight: 600;
  color: #3159B3;
  line-height: 37px;
  margin: 0 0 20px 0;
}

.section-text {
  font-size: 16px;
  color: #333333;
  line-height: 22px;
}

.section-divider {
  width: 1090px;
  height: 1px;
  background-color: #E5E5E5;
  margin: 30px 0;
}

.article-list .article-item {
  margin-bottom: 15px;
  cursor: pointer;
}

.article-list .article-item a {
  color: #333333;
  text-decoration: none;
  transition: color 0.3s ease;
  line-height: 22px;
}

.article-list .article-item a:hover {
  color: #3159B3;
}

/* 新增：文章列表样式 */
.loading-articles,
.no-articles {
  color: #999999;
  font-size: 14px;
  text-align: center;
  padding: 20px 0;
}

.article-items {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.article-item {
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  transition: all 0.3s ease;
  border: 1px solid transparent;
}

.article-item:hover {
  background: #f0f2f5;
  border-color: #e1e5e9;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.article-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.article-title {
  font-size: 16px;
  font-weight: 500;
  color: #333333;
  text-decoration: none;
  line-height: 1.4;
  transition: color 0.3s ease;
}

.article-title:hover {
  color: #3159B3;
}

.article-meta {
  display: flex;
  gap: 16px;
  font-size: 14px;
  color: #666666;
}

.article-date,
.article-author {
  display: flex;
  align-items: center;
}

.article-date::before {
  margin-right: 4px;
}

.article-author::before {
  margin-right: 4px;
}
</style>
